import styled, { css } from 'styled-components'
import {
  accessibilityZIndex,
  colors,
  device,
  fonts,
} from '../../../styles/GlobalVariables'
import { RoundButton } from './StoriesOverlay.style'

/**
 * The viewer's window for the screens shown instead of it: full screen on
 * mobile; from 1024 the 514x914 player, 914px tall at most and 83px from the
 * window's top and bottom in a 1080 window, the same shares in another (as
 * StoriesViewer sizes the stage), on 95% black.
 */
const desktop = device.regular
/** the viewer's own height (StoriesViewer): 914 of a 1080 window, at most 914px */
const PLAYER_HEIGHT = 'min(914px, calc(100vh * 914 / 1080))'

export const ScreenBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${accessibilityZIndex + 1};
  height: 100vh;
  height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #000;
  overscroll-behavior: contain;
  font-family: ${fonts.narkis};

  &:focus {
    outline: none;
  }

  ${desktop} {
    height: 100vh;
    background-color: rgba(0, 0, 0, 0.95);
  }
`

export const ScreenStage = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;

  /* below 1024 on a screen wider than a portrait phone (a phone on its side,
     a tablet): the full height and a 9:16 width, as the viewer */
  @media (min-width: 540px) and (max-width: 1023.98px) and (min-aspect-ratio: 5 / 8) {
    width: calc(100dvh * 9 / 16);
  }

  ${desktop} {
    width: calc(${PLAYER_HEIGHT} * 514 / 914);
    height: ${PLAYER_HEIGHT};
  }
`

/**
 * Figma "M_Ad blocker" / "D_Ad blocker" Close (24339:376900, 24339:378863):
 * the viewer's round button, 12px from the corner on mobile and 20px on
 * desktop
 */
export const ScreenClose = styled(RoundButton)`
  position: absolute;
  top: calc(12px + env(safe-area-inset-top, 0px));
  right: 12px;
  z-index: 1;

  ${desktop} {
    top: 20px;
    right: 20px;
  }
`

/** the frame the clip opens on, as the viewer's player paints it until it plays */
export const LoadingPoster = styled.div`
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center center;
  background-repeat: no-repeat;
`

/**
 * Figma "M_Ad blocker" (24237:374777, 375x666) and the panel of "D_Ad
 * blocker" (24339:378813, 514x914): a white to #d0d0d0 radial light, the stop
 * hand faint and turned behind the text, the text 80px from the top (desktop
 * 100px) and the 13 logo 60px above the bottom.
 */
/**
 * The viewer's place for a screen with the side buttons beside it (desktop):
 * the frame does not clip, the screen inside it does
 */
export const ScreenFrame = styled(ScreenStage)`
  overflow: visible;
`

const ScreenFill = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
`

export const AdBlockStage = styled(ScreenFill)`
  background: radial-gradient(closest-side, #fff, #d0d0d0);
  color: ${colors.darkBlue};
  text-align: right;
`

export const AdBlockHand = styled.img`
  position: absolute;
  /* "M_Ad Blocker" 2026-10-20: 528.11px, its corner at -248.73, 143.61
     (turned about that corner in Figma; here the same box about its centre) */
  top: 93.91px;
  left: -207px;
  width: 528.11px;
  height: 528.11px;
  object-fit: cover;
  opacity: 0.04;
  /* not narrowed to the stage by the global img rule */
  max-width: none;
  transform: rotate(-9.97deg);
  pointer-events: none;

  /* "D_Ad Blocker" 2026-10-19 evening: 837px, its corner at -332.17, 187.16
     (turned about that corner in Figma; here the same box about its centre) */
  ${desktop} {
    top: 108.38px;
    left: -266.03px;
    width: 837px;
    height: 837px;
  }
`

/** the text over the logo; it scrolls inside the stage when the window is short */
export const AdBlockScroll = styled.div`
  position: relative;
  height: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  padding: calc(70px + env(safe-area-inset-top, 0px)) 20px
    calc(60.051px + env(safe-area-inset-bottom, 0px));
  box-sizing: border-box;

  ${desktop} {
    padding: 80px 20px 59.577px;
  }
`

export const AdBlockText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;

  ${desktop} {
    gap: 40px;
  }
`

/**
 * The design's lines are the font's normal height (2026-10-19), written out
 * so every engine draws the same boxes: Narkiss at 1.2, rounded as Figma does
 */
const line = (px: number) => css`
  margin: 0;
  line-height: ${px}px;
`

export const AdBlockIntro = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  h2 {
    ${line(31)}
    font-weight: 600;
    font-size: 26px;
  }

  p {
    ${line(22)}
    font-size: 18px;
  }

  ${desktop} {
    h2 {
      ${line(41)}
      font-size: 34px;
    }

    p {
      ${line(24)}
      font-size: 20px;
    }
  }
`

export const AdBlockGroups = styled.div`
  display: flex;
  flex-direction: column;
  gap: 28px;

  ${desktop} {
    gap: 32px;
  }
`

export const AdBlockGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  h3 {
    ${line(24)}
    font-weight: 600;
    font-size: 20px;
  }

  ul {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  /* the 20px icon (the design's Icons set) stands at the first line's top */
  li {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 16px;
    line-height: 19px;
  }

  li img,
  li svg {
    flex-shrink: 0;
  }

  li img {
    width: 20px;
    height: 20px;
  }

  b {
    font-weight: 600;
  }

  ${desktop} {
    gap: 10px;

    h3 {
      ${line(29)}
      font-size: 24px;
    }

    ul {
      gap: 14px;
    }

    li {
      font-size: 18px;
      line-height: 22px;
    }
  }
`

/**
 * 40 x 42.949, half a pixel right of the centre; on desktop the same file at
 * 1.5 (60 x 64.423, centred), as the desktop frame draws it. The file is
 * larger by its glow (8.97% on top, 7.28% on the right).
 */
export const AdBlockLogo = styled.span`
  position: relative;
  flex-shrink: 0;
  align-self: center;
  width: 40px;
  height: 42.949px;
  margin: auto 0 0 1px;
  padding-top: 40px;
  box-sizing: content-box;

  /* the glow passes the box */
  svg {
    position: absolute;
    bottom: 0;
    left: 0;
  }

  ${desktop} {
    width: 60px;
    height: 64.423px;
    margin-left: 0;
  }
`

/**
 * Figma "M_Geo Block" (24497:410939, 375x666) and "D_Geo Block" (24497:405712,
 * the 514x914 player): the clips do not play outside Israel (D45). A radial
 * navy light, the 13 faint and turned behind the text, the text near the top
 * and the white 13 60px above the bottom.
 */
export const GeoStage = styled(ScreenFill)`
  background: radial-gradient(
    50% 50% at 50% 50%,
    #102985 0%,
    #081d68 50%,
    #00104a 100%
  );
  color: #fff;
  text-align: center;
`

/** 3%, turned 12.9deg about its centre (mobile 518.378 x 529.897) */
export const GeoMark = styled.img`
  position: absolute;
  top: calc(50% - 16.88px);
  left: calc(50% - 7.83px);
  width: 518.378px;
  height: 529.897px;
  max-width: none;
  opacity: 0.03;
  transform: translate(-50%, -50%) rotate(12.9deg);
  pointer-events: none;

  /* 680.96 x 696.09, its turned corner at 2, 73: the centre 0.81px left of
     the stage's and 31.27px below it */
  ${desktop} {
    top: calc(50% + 31.27px);
    left: calc(50% - 0.81px);
    width: 680.96px;
    height: 696.09px;
  }
`

/** 26px (desktop 34) semibold, the font's own line height, two centred lines */
export const GeoText = styled.h2`
  position: absolute;
  top: calc(160px + env(safe-area-inset-top, 0px));
  left: 20px;
  right: 20px;
  margin: 0;
  font-weight: 600;
  font-size: 26px;
  line-height: 31px;

  ${desktop} {
    top: 200px;
    font-size: 34px;
    line-height: 41px;
  }
`

/** the white 13: mobile 40 x 42.892 half a pixel right of the centre, desktop 60 x 64.34 */
export const GeoLogo = styled.img`
  position: absolute;
  bottom: calc(60.108px + env(safe-area-inset-bottom, 0px));
  left: calc(50% + 0.5px);
  width: 40px;
  height: 42.892px;
  max-width: none;
  transform: translateX(-50%);

  ${desktop} {
    bottom: 60.66px;
    left: 50%;
    width: 60px;
    height: 64.34px;
  }
`
